
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { UseFormReturn } from "react-hook-form";
import { WelcomeFormValues } from "./types";

const BIBLE_VERSIONS = [
  { id: "KJV", name: "King James Version" },
  { id: "NASB", name: "New American Standard Bible" },
  { id: "9879dbb7cfe39e4d-01", name: "World English Bible" },
] as const;

interface WelcomeFormProps {
  form: UseFormReturn<WelcomeFormValues>;
  onNext: () => void;
  hasChurchCode?: boolean;
}

export function WelcomeForm({ form, onNext, hasChurchCode = false }: WelcomeFormProps) {
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const formValues = form.getValues();
    
    // Check if name is actually filled out
    if (!formValues.full_name || formValues.full_name.trim().length < 2) {
      form.setError("full_name", {
        type: "manual",
        message: "Name must be at least 2 characters"
      });
      return;
    }
    
    // Clear any existing errors
    form.clearErrors();
    onNext();
  };

  return (
    <div className="space-y-6 w-full animate-fade-in">
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-semibold text-grace-dark">Welcome to Grace Awakens</h2>
        <p className="text-muted-foreground">Please tell us a bit about yourself to get started</p>
      </div>

      <Form {...form}>
        <form onSubmit={handleSubmit} className="space-y-6">
          <FormField
            control={form.control}
            name="full_name"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-grace-dark">Your Full Name <span className="text-red-500">*</span></FormLabel>
                <FormControl>
                  <Input 
                    placeholder="Enter your full name" 
                    {...field} 
                    className="border-grace-light focus:border-grace-DEFAULT"
                    required
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="congregation_name"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-grace-dark">
                  Congregation Name {!hasChurchCode && "(Optional)"}
                </FormLabel>
                <FormControl>
                  <Input 
                    placeholder="Enter your congregation name" 
                    {...field}
                    className="border-grace-light focus:border-grace-DEFAULT"
                    readOnly={hasChurchCode}
                    disabled={hasChurchCode}
                  />
                </FormControl>
                {hasChurchCode && (
                  <p className="text-xs text-gray-500 mt-1">
                    This congregation is associated with your bracelet code.
                  </p>
                )}
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="bible_version"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-grace-dark">Bible Version</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger className="border-grace-light focus:border-grace-DEFAULT">
                      <SelectValue placeholder="Select Bible version" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {BIBLE_VERSIONS.map((version) => (
                      <SelectItem key={version.id} value={version.id}>
                        {version.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-sm text-muted-foreground">
                  Choose your preferred Bible translation
                </p>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button 
            type="submit"
            className="w-full bg-grace-DEFAULT hover:bg-grace-dark text-white font-semibold py-3 transition-colors shadow-md"
          >
            {hasChurchCode ? "Complete Setup" : "Continue"}
          </Button>
        </form>
      </Form>
    </div>
  );
}
